import { useAdminMarket } from "@/hooks/useAdminMarket";
import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Plug, PlugZap, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Props {
  className?: string;
  compact?: boolean;
}

export function ToconlineConnectionButton(props: Props) {
  const { market } = useAdminMarket();
  if (market === "br") return null; // TOConline does not exist in Brazil
  return <ToconlineConnectionButtonInner {...props} />;
}

function ToconlineConnectionButtonInner({ className, compact }: Props) {
  const [connected, setConnected] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const { data } = await supabase.functions.invoke("toconline-auth", {
        body: { action: "status" },
      });
      setConnected(!!data?.connected);
      return !!data?.connected;
    } catch {
      setConnected(false);
      return false;
    }
  }, []);

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  const handleClick = async () => {
    if (connected) {
      const ok = await checkStatus();
      toast[ok ? "success" : "error"](
        ok ? "TOConline ligado." : "TOConline desligado. Clique para ligar.",
      );
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("toconline-auth", {
        body: { action: "authorize" },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("Não foi possível obter o link de autorização");

      const popup = window.open(data.url, "_blank", "width=600,height=700");
      toast.info("Complete a autorização na janela aberta.");

      const started = Date.now();
      const timer = setInterval(async () => {
        const expired = Date.now() - started > 5 * 60 * 1000;
        if (popup && !popup.closed && !expired) return;
        clearInterval(timer);
        const ok = await checkStatus();
        if (ok) toast.success("TOConline ligado com sucesso.");
      }, 2000);
    } catch (err: any) {
      toast.error("Erro ao ligar ao TOConline: " + (err?.message || err));
    } finally {
      setBusy(false);
    }
  };

  const isConnected = connected === true;
  const label = connected === null ? "TOConline" : isConnected ? "TOConline ligado" : "Ligar TOConline";

  return (
    <Button
      size="sm"
      onClick={handleClick}
      disabled={busy}
      title={label}
      className={cn(
        "h-8 gap-1.5 text-white",
        isConnected
          ? "bg-success hover:bg-success/90"
          : "bg-destructive hover:bg-destructive/90",
        className,
      )}
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : isConnected ? (
        <PlugZap className="h-3.5 w-3.5" />
      ) : (
        <Plug className="h-3.5 w-3.5" />
      )}
      {!compact && <span className="hidden md:inline">{label}</span>}
    </Button>
  );
}
